import { Input } from "design-system/atoms/Input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useBigBlueIntegrationQuery } from "modules/settings/hooks/bigblue/useBigBlueIntegrationQuery"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  BIGBLUE_BACKORDER_DEFAULT_CONFIG,
  BigBlueBackorderSkuMatchStrategyEnum,
  BigBlueCampaignTrigger,
  BigBlueCampaignTriggerEnum,
  areBigBlueWebhooksWorking,
  getEmailCampaignChannelIntegration,
} from "schema"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>

const BIGBLUE_TRIGGER_OPTIONS = [
  BigBlueCampaignTriggerEnum.Backorder,
  BigBlueCampaignTriggerEnum.Exception,
  BigBlueCampaignTriggerEnum.CarrierReturn,
] as const

export function BigBlueTriggerForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const { data: bigBlueIntegration } = useBigBlueIntegrationQuery()
  const triggerIntegration = form.watch("triggerIntegration")
  const bigblue = triggerIntegration?.type === "bigblue" ? triggerIntegration : undefined
  const backorder =
    bigblue?.trigger === BigBlueCampaignTriggerEnum.Backorder ? bigblue.backorder : null
  const email = getEmailCampaignChannelIntegration(triggerIntegration)
  const areWebhooksWorking = areBigBlueWebhooksWorking(bigBlueIntegration)
  const isFormDisabled = isDisabled || !areWebhooksWorking

  if (!areWebhooksWorking) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("campaigns.trigger.metadata.bigblue.disabledDescription")}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4 w-full md:max-w-[70vw]">
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={() => (
          <FormItem className="flex flex-col gap-2">
            <FormLabel>{t("campaigns.trigger.metadata.bigblue.trigger.label")}</FormLabel>
            <FormControl>
              <Select
                value={bigblue?.trigger}
                onValueChange={(value) => {
                  const trigger = value as BigBlueCampaignTrigger
                  const nextIntegration = {
                    type: "bigblue" as const,
                    trigger,
                    email: bigblue?.email ?? email,
                    ...(trigger === BigBlueCampaignTriggerEnum.Backorder && {
                      backorder: BIGBLUE_BACKORDER_DEFAULT_CONFIG,
                    }),
                  }
                  form.setValue("triggerIntegration", nextIntegration, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                  form.clearErrors([
                    "triggerIntegration.backorder.incomingThreshold",
                    "triggerIntegration.backorder.skuMatchStrategy",
                  ])
                }}
                disabled={isFormDisabled}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("campaigns.trigger.metadata.bigblue.trigger.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {BIGBLUE_TRIGGER_OPTIONS.map((trigger) => (
                    <SelectItem key={trigger} value={trigger}>
                      {t(`campaigns.trigger.metadata.bigblue.trigger.options.${trigger}.label`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            {bigblue?.trigger && (
              <p className="text-sm text-muted-foreground">
                {t(
                  `campaigns.trigger.metadata.bigblue.trigger.options.${bigblue.trigger}.description`,
                )}
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      {bigblue?.trigger === BigBlueCampaignTriggerEnum.Backorder && backorder && (
        <>
          <FormField
            control={form.control}
            name="triggerIntegration.backorder.incomingThreshold"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1">
                <FormLabel>
                  {t("campaigns.trigger.metadata.bigblue.backorder.incomingThreshold.label")}
                </FormLabel>
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.trigger.metadata.bigblue.backorder.incomingThreshold.description")}
                </p>
                <FormControl>
                  <Input
                    {...field}
                    className="w-32"
                    wrapperClassName="w-auto"
                    disabled={isFormDisabled}
                    min={0}
                    step={1}
                    type="number"
                    value={backorder.incomingThreshold ?? ""}
                    rightText={t(
                      "campaigns.trigger.metadata.bigblue.backorder.incomingThreshold.suffix",
                    )}
                    onChange={(event) => {
                      const value = event.target.value
                      field.onChange(value === "" ? "" : Number(value))
                    }}
                  />
                </FormControl>
                <FormMessage>
                  {t("campaigns.trigger.metadata.bigblue.backorder.incomingThreshold.invalid")}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="triggerIntegration.backorder.skuMatchStrategy"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1">
                <FormLabel>
                  {t("campaigns.trigger.metadata.bigblue.backorder.skuMatchStrategy.label")}
                </FormLabel>
                <FormControl>
                  <Select
                    value={backorder.skuMatchStrategy}
                    onValueChange={field.onChange}
                    disabled={isFormDisabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={BigBlueBackorderSkuMatchStrategyEnum.AtLeastOne}>
                        {t(
                          "campaigns.trigger.metadata.bigblue.backorder.skuMatchStrategy.options.atLeastOne",
                        )}
                      </SelectItem>
                      <SelectItem value={BigBlueBackorderSkuMatchStrategyEnum.All}>
                        {t(
                          "campaigns.trigger.metadata.bigblue.backorder.skuMatchStrategy.options.all",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.trigger.metadata.bigblue.backorder.skuMatchStrategy.description")}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  )
}
